import { useState, useMemo, useEffect } from "react";
import { AlertCircle, Check, ChevronDown, ChevronRight, Clock, Copy, Loader2, RefreshCw, Sparkles, SquarePen, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SectionSourceModal from "@/components/SectionSourceModal";
import { GeneratedContentView, type ArtifactAppendix, type GeneratedContent } from "./GeneratedContentView";
import { SectionOutlinePanel } from "./SectionOutlinePanel";
import { useSectionListState } from "./useSectionListState";
import type { SectionStatus } from "@/hooks/useReportGeneration";
import type { Section } from "./SortableRow";
import type { GridReviewMap } from "@/lib/extractionReview";
import type { Document } from "@/types/models";
import { projectsApi, type Artifact } from "@/services/projectsApi";
import { dropUnsupportedCitations } from "@/utils/parseCitations";

type ProjectTemplateData = Awaited<ReturnType<typeof projectsApi.getProjectTemplate>>;

interface ReportDocumentViewProps {
  sections: Section[];
  generatedContent: Record<string, GeneratedContent>;
  sectionStatuses: Record<string, SectionStatus>;
  documents?: Document[];
  onGenerate: (sectionId: string) => void;
  onRegenerate: (sectionId: string) => void;
  onSaveEdit: (sectionId: string, newText: string) => void;
  onSaveName: (sectionId: string, newName: string) => void;
  filterSectionId?: string | null;
  projectId: string;
  activeTemplate: NonNullable<ProjectTemplateData>;
  templateQueryKey: string[];
  extractionPoints: { id: string; name: string; parent_id?: string | null }[];
  extractionDocuments: { documentId: string; projectTemplateId: string }[];
  gridReview: GridReviewMap;
  isExtractionLoading?: boolean;
  hasExtractionError?: boolean;
  artifacts?: Artifact[];
  /** Artefacts shown live below each drafted section, keyed by section id. */
  artifactAppendix?: Record<string, ArtifactAppendix>;
  originalPrimingMap: Record<string, string | null>;
  onSelectPrompt: (section: Section) => void;
  onSaveInstruction: (sectionId: string, value: string) => void;
  onMissingSourcesChange?: (hasMissingSources: boolean) => void;
}

export const ReportDocumentView = ({
  sections,
  generatedContent,
  sectionStatuses,
  documents,
  onGenerate,
  onRegenerate,
  onSaveEdit,
  onSaveName,
  filterSectionId,
  projectId,
  activeTemplate,
  templateQueryKey,
  extractionPoints,
  extractionDocuments,
  gridReview,
  isExtractionLoading,
  hasExtractionError,
  artifacts,
  artifactAppendix,
  originalPrimingMap,
  onSelectPrompt,
  onSaveInstruction,
  onMissingSourcesChange,
}: ReportDocumentViewProps) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [collapsedContentIds, setCollapsedContentIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [openPanelIds, setOpenPanelIds] = useState<Set<string>>(new Set());

  const {
    selectedSources,
    sourceModalSectionId,
    setSourceModalSectionId,
    handleSourceSave,
  } = useSectionListState({ projectId, activeTemplate, templateQueryKey, stage: "Report Writing" });

  const togglePanel = (id: string) => {
    setOpenPanelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
    // Opening/closing the outline panel always brings the section's own content back into view.
    setCollapsedContentIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleContentCollapse = (id: string) =>
    setCollapsedContentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });

  const handleCopy = (sectionId: string, content: GeneratedContent, label: string, sectionName: string) => {
    const heading = `${label}. ${sectionName}`;
    const container = document.querySelector<HTMLElement>(
      `[data-section-content="${sectionId}"] [data-markdown-body]`
    );
    const citationLines = content.citations.map((c) => `[${c.index}] ${c.label}`);
    // Strip markdown syntax and inline citation markers (^1^) for plain text output
    const strippedBodyText = dropUnsupportedCitations(content.text)
      .replace(/\[[^\]]*\.(?:pdf|docx|pptx|xlsx)[^\]]*\]/gi, "")
      .replace(/\^\d+\^/g, "")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\*{1,3}([^*\n]+)\*{1,3}/g, "$1")
      .replace(/_{1,3}([^_\n]+)_{1,3}/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/^[-*+]\s+/gm, "")
      .replace(/^\d+\.\s+/gm, "")
      .replace(/^>\s*/gm, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    // Artefacts by caption — their content is a table or image, not plain text.
    const artifactNames = artifactAppendix?.[sectionId]?.names ?? [];
    const plainText = [
      heading,
      "",
      strippedBodyText,
      ...(artifactNames.length > 0 ? ["", ...artifactNames] : []),
      ...(citationLines.length > 0 ? ["", ...citationLines] : []),
    ].join("\n");

    if (container) {
      const citationsHtml = citationLines.length > 0
        ? `<p>${citationLines.join("<br>")}</p>`
        : "";
      // Clone the node so we can inject inline table styles without mutating the DOM.
      // CSS classes are lost when pasting into Word/Outlook, so borders must be inline.
      const clone = container.cloneNode(true) as HTMLElement;
      clone.querySelectorAll("table").forEach((t) => {
        t.style.borderCollapse = "collapse";
        t.style.width = "100%";
      });
      clone.querySelectorAll("th, td").forEach((cell) => {
        (cell as HTMLElement).style.border = "1px solid #d1d5db";
        (cell as HTMLElement).style.padding = "6px 12px";
      });
      clone.querySelectorAll("th").forEach((th) => {
        (th as HTMLElement).style.backgroundColor = "#f3f4f6";
        (th as HTMLElement).style.fontWeight = "600";
      });
      const fullHtml = `<h2>${heading}</h2>${clone.innerHTML}${citationsHtml}`;
      navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([fullHtml], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
    } else {
      navigator.clipboard.writeText(plainText);
    }

    setCopiedId(sectionId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleCollapse = (id: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });

  const scrollToSection = (id: string) => {
    requestAnimationFrame(() => {
      document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "instant", block: "nearest" });
    });
  };

  const startEditing = (section: Section) => {
    setEditingId(section.id);
    setEditingNameValue(section.name);
    scrollToSection(section.id);
  };

  const handleSaveEdit = (section: Section, newText: string) => {
    onSaveEdit(section.id, newText);
    const newName = editingNameValue.trim();
    if (newName && newName !== section.name) {
      onSaveName(section.id, newName);
    }
    setEditingId(null);
    setEditingNameValue("");
    scrollToSection(section.id);
  };

  const handleCancelEdit = () => {
    const id = editingId;
    setEditingId(null);
    setEditingNameValue("");
    if (id) scrollToSection(id);
  };

  // Build label map in a single pass (same logic as SectionList)
  const { labelMap, parentIds } = useMemo(() => {
    const labelMap = new Map<string, string>();
    const rootNumbers = new Map<string, number>();
    const childCounters = new Map<string, number>();
    let rootCount = 0;
    for (const s of sections) {
      if (s.parent_id === null) {
        rootCount++;
        rootNumbers.set(s.id, rootCount);
        labelMap.set(s.id, `${rootCount}`);
      } else {
        const parentNum = rootNumbers.get(s.parent_id) ?? 0;
        const childNum = (childCounters.get(s.parent_id) ?? 0) + 1;
        childCounters.set(s.parent_id, childNum);
        labelMap.set(s.id, `${parentNum}.${childNum}`);
      }
    }
    const parentIds = new Set(sections.filter((s) => s.parent_id !== null).map((s) => s.parent_id));
    return { labelMap, parentIds };
  }, [sections]);

  const filterSection = filterSectionId ? sections.find((s) => s.id === filterSectionId) : null;
  const visibleSections = sections.filter((s) => {
    if (filterSection) {
      if (filterSection.parent_id === null) {
        if (s.id !== filterSection.id && s.parent_id !== filterSection.id) return false;
      } else {
        if (s.id !== filterSection.id) return false;
      }
    }
    return s.parent_id === null || !collapsedSections.has(s.parent_id);
  });

  const hasSectionsMissingSources = sections.some((s) => {
    const src = selectedSources[s.id] ?? { documentIds: [], extractionPointIds: [], artifactIds: [] };
    return !generatedContent[s.id] && src.documentIds.length === 0 && src.extractionPointIds.length === 0 && src.artifactIds.length === 0;
  });

  useEffect(() => {
    onMissingSourcesChange?.(hasSectionsMissingSources);
  }, [hasSectionsMissingSources, onMissingSourcesChange]);

  return (
    <div className="flex flex-col gap-3">
      {visibleSections.map((section) => {
        const label = labelMap.get(section.id) ?? "";
        const isChild = !!section.parent_id;
        const isCollapsible = !isChild && parentIds.has(section.id);
        const isCollapsed = collapsedSections.has(section.id);
        const isEditing = editingId === section.id;
        const content = generatedContent[section.id];
        const sectionStatus = sectionStatuses[section.id]?.status;
        const queuePosition = sectionStatuses[section.id]?.queuePosition;
        const queueSize = sectionStatuses[section.id]?.queueSize;
        const sectionError = sectionStatuses[section.id]?.error;
        const isGenerating = sectionStatus === "Processing" || (sectionStatus === "Queued" && (queueSize ?? 0) > 0);
        const isPanelOpen = openPanelIds.has(section.id);
        const isContentCollapsed = collapsedContentIds.has(section.id);
        const sectionSources = selectedSources[section.id] ?? { documentIds: [], extractionPointIds: [], artifactIds: [] };
        const hasNoSources = !content && sectionSources.documentIds.length === 0 && sectionSources.extractionPointIds.length === 0 && sectionSources.artifactIds.length === 0;

        return (
          <div
            key={section.id}
            id={`section-${section.id}`}
            className={
              `group bg-white border overflow-hidden [overflow-anchor:none] grid ${isPanelOpen && !isContentCollapsed ? "grid-cols-[1fr_380px]" : "grid-cols-1"} ${isEditing ? "border-brand-teal/80 bg-brand-teal/5" : hasNoSources ? "border-red-300" : "hover:border-brand-teal/40"} ${isChild ? "ml-6" : ""}`
            }
          >
            <div
              className="flex flex-col min-w-0"
            >
              {/* Section header */}
              <div className="flex items-center justify-between px-4 py-3 ">
                <div className="grow flex items-center gap-2 min-w-0">
                  {isCollapsible && (
                    <button
                      onClick={() => toggleCollapse(section.id)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      tabIndex={-1}
                    >
                      {isCollapsed
                        ? <ChevronRight className="w-4 h-4" />
                        : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                  <div
                    className={`flex items-center gap-2 min-w-0 ${!isEditing ? "cursor-pointer" : ""}`}
                    onClick={() => !isEditing && toggleContentCollapse(section.id)}
                  >
                    <span className="font-semibold text-base shrink-0">{label}.</span>
                    {isEditing ? (
                      <Input
                        className="font-semibold text-base flex-1"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Escape" && setEditingNameValue(section.name)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <span className="font-semibold text-base truncate">{section.name}</span>
                    )}
                  </div>
                </div>

                {/* Action buttons — hidden while editing or while the outline panel is open */}
                <div className="flex items-center gap-2 shrink-0">
                  {!isPanelOpen && !isEditing && (
                    <Button
                      size="sm"
                      variant="brand"
                      appearance="text"
                      className="text-xs gap-1"
                      onClick={() => togglePanel(section.id)}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1" />Edit instructions
                    </Button>
                  )}
                  {!isPanelOpen && content && !isEditing && (
                    <Button
                      size="sm"
                      variant="brand"
                      appearance="text"
                      className="text-xs gap-1"
                      onClick={() => onRegenerate(section.id)}
                      disabled={isGenerating}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                    </Button>
                  )}
                  {!isPanelOpen && !content && (
                    <Button
                      size="sm"
                      variant="brand"
                      appearance="text"
                      disabled={isGenerating}
                      onClick={() => onGenerate(section.id)}
                    >
                      {sectionStatus === "Queued" ? (
                        queueSize === 0
                          ? <><Sparkles className="w-3 h-3 mr-1" />Generate</>
                          : <><Clock className="w-3 h-3 mr-1" />{queuePosition && queueSize ? `Queued ${queuePosition} of ${queueSize}` : "Queued"}</>
                      ) : sectionStatus === "Processing" ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Generating…</>
                      ) : (
                        <><Sparkles className="w-3 h-3 mr-1" />Generate</>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {!isContentCollapsed && isPanelOpen && !content && !sectionStatus && (
                <div className="flex flex-column justify-center items-center grow">
                  
                    <div className="flex flex-col grow items-center text-center">
                      
                        <Sparkles className="w-7 h-7 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="font-semibold text-sm">No content yet</p>
                          <p className="text-sm mt-0.5">
                            Configure sources and AI instructions on the right, then click Generate.
                          </p>
                        </div>
                      
                    </div>
                  
                </div>
              )}

              {!isContentCollapsed && (content || sectionStatus) && (
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0 flex flex-col gap-3">
                    {/* Queued state */}
                    {sectionStatus === "Queued" && !content && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4 shrink-0" />
                        {queuePosition && queueSize
                          ? `Queued — position ${queuePosition} of ${queueSize}`
                          : "Queued — waiting to generate…"}
                      </div>
                    )}

                    {/* Processing state */}
                    {sectionStatus === "Processing" && !content && (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                          Generating content…
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-muted rounded animate-pulse w-full" />
                          <div className="h-3 bg-muted rounded animate-pulse w-5/6" />
                          <div className="h-3 bg-muted rounded animate-pulse w-4/6" />
                        </div>
                      </div>
                    )}

                    {/* Error state */}
                    {sectionStatus === "Error" && !content && (
                      <div className="flex items-start gap-2 text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          {sectionError === "No content could be retrieved for any of the requested documents"
                            ? hasNoSources
                              ? "No sources are configured for this section. Click Edit instructions and select at least one document, extraction point or artefact before generating."
                              : "None of the selected sources returned any content. Check that the documents are indexed and the artefacts are Ready, then regenerate."
                            : (sectionError ?? "Generation failed. Try regenerating the section.")}
                        </div>
                      </div>
                    )}

                    {/* Generated content */}
                    {content && (
                      <div data-section-content={section.id}>
                        {!isEditing && (
                          <div className="flex justify-end items-center gap-2 mb-1">
                            <Button
                              size="sm"
                              variant="secondary"
                              appearance="text"
                              className="text-xs gap-1"
                              onClick={() => startEditing(section)}
                            >
                              <SquarePen className="w-3 h-3 mr-1" />Edit
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className={`h-9 w-9 transition-opacity ${copiedId === section.id ? "opacity-100" : "opacity-40 group-hover:opacity-100"}`}
                              onClick={() => handleCopy(section.id, content, label, section.name)}
                              title="Copy content"
                            >
                              {copiedId === section.id
                                ? <Check className="w-3 h-3 text-green-600" />
                                : <Copy className="w-3 h-3" />}
                            </Button>
                          </div>
                        )}
                        <GeneratedContentView
                          content={content}
                          artifactAppendix={artifactAppendix?.[section.id]}
                          onRegenerate={() => onRegenerate(section.id)}
                          onEdit={() => startEditing(section)}
                          isRegenerating={isGenerating}
                          hideToolbar
                          isEditing={isEditing}
                          onSaveEdit={(newText) => handleSaveEdit(section, newText)}
                          onCancelEdit={handleCancelEdit}
                          documents={documents}
                        />
                      </div>
                    )}
                  </div>


                </div>
              )}
            </div>

            {isPanelOpen && !isContentCollapsed && (
              <SectionOutlinePanel
                section={section}
                sectionSources={sectionSources}
                documents={documents ?? []}
                extractionPoints={extractionPoints}
                artifacts={artifacts}
                gridReview={gridReview}
                onOpenSources={() => setSourceModalSectionId(section.id)}
                onSelectPrompt={() => onSelectPrompt(section)}
                onSaveInstruction={onSaveInstruction}
                onGenerate={() => (content ? onRegenerate(section.id) : onGenerate(section.id))}
                sectionStatus={sectionStatus}
                queuePosition={queuePosition}
                queueSize={queueSize}
                originalPriming={originalPrimingMap[section.id] ?? null}
                hasContent={!!content}
                onClose={() => togglePanel(section.id)}
              />
            )}
          </div>
        );
      })}

      {sourceModalSectionId && (() => {
        const current = selectedSources[sourceModalSectionId] ?? { documentIds: [], extractionPointIds: [], artifactIds: [] };
        const section = sections.find((s) => s.id === sourceModalSectionId);
        return (
          <SectionSourceModal
            open={true}
            onClose={() => setSourceModalSectionId(null)}
            sectionName={section?.name ?? ""}
            documents={documents ?? []}
            extractionPoints={extractionPoints}
            extractionDocuments={extractionDocuments}
            gridReview={gridReview}
            isExtractionLoading={isExtractionLoading}
            hasExtractionError={hasExtractionError}
            selectedDocumentIds={current.documentIds}
            selectedExtractionPointIds={current.extractionPointIds}
            artifacts={artifacts}
            selectedArtifactIds={current.artifactIds ?? []}
            onSave={handleSourceSave}
          />
        );
      })()}
    </div>
  );
};
